import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../api/api';
import { SnackbarContext } from '../App';
import { ClipboardList, Plus, Users, Globe, Lock, MoreHorizontal, ChevronRight, Search, Clock, Edit2, Trash2 } from "lucide-react";
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import PageHeader from './ui/PageHeader';
import PageShell from './ui/PageShell';
import EmptyState from './ui/EmptyState';
import StatusBadge from './ui/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { useConfirm } from '../contexts/ConfirmContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";

export default function CrmLists() {
    const showSnackbar = useContext(SnackbarContext);
    const navigate = useNavigate();
    const openConfirm = useConfirm();
    
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({ name: '', description: '', visibility: 'PRIVATE' });
    const [editingList, setEditingList] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchLists = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/prospect-lists');
            setLists(Array.isArray(data.data) ? data.data : []);
        } catch (error) {
            console.error('Error fetching lists:', error);
            showSnackbar('Error loading prospect lists', 'error');
        } finally {
            setLoading(false);
        }
    }, [showSnackbar]);

    useEffect(() => {
        fetchLists();
    }, [fetchLists]);

    const handleCreateList = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return showSnackbar('List name is required', 'error');
        
        setIsSubmitting(true);
        try {
            await api.post('/api/prospect-lists', formData);
            showSnackbar('List created successfully', 'success');
            setIsCreateModalOpen(false);
            setFormData({ name: '', description: '', visibility: 'PRIVATE' });
            fetchLists();
            // Optional: navigate to the new list immediately
            // navigate(`/crm/lists/${response.data.data._id}`);
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Error creating list', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEditList = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return showSnackbar('List name is required', 'error');
        
        setIsSubmitting(true);
        try {
            await api.put(`/api/prospect-lists/${editingList._id}`, formData);
            showSnackbar('List updated successfully', 'success');
            setIsEditModalOpen(false);
            setEditingList(null);
            setFormData({ name: '', description: '', visibility: 'PRIVATE' });
            fetchLists();
        } catch (err) {
            showSnackbar(err.isAppError ? err.message : 'Error updating list', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteList = (list) => {
        openConfirm({
            title: 'Delete List?',
            message: `Are you sure you want to delete "${list.name}"? This will not delete the contacts, but it will permanently remove the list and their membership in it.`,
            confirmText: 'Delete List',
            variant: 'destructive',
            onConfirm: async () => {
                try {
                    await api.delete(`/api/prospect-lists/${list._id}`);
                    showSnackbar('List deleted successfully', 'success');
                    fetchLists();
                } catch (err) {
                    showSnackbar(err.isAppError ? err.message : 'Error deleting list', 'error');
                }
            }
        });
    };

    const openEditModal = (e, list) => {
        e.stopPropagation(); // Prevent card click
        setEditingList(list);
        setFormData({ name: list.name, description: list.description || '', visibility: list.visibility || 'PRIVATE' });
        setIsEditModalOpen(true);
    };

    const filteredLists = lists.filter(l => l.name.toLowerCase().includes(searchQuery.toLowerCase()));

    return (
        <PageShell>
            <PageHeader 
                title="Lists"
                subtitle="Organize your target leads into segmented CRM lists for automated outreach."
                icon={ClipboardList}
                actions={
                    <Button onClick={() => {
                        setFormData({ name: '', description: '', visibility: 'PRIVATE' });
                        setIsCreateModalOpen(true);
                    }} className="bg-primary hover:bg-primary/90 shadow-lg border-0 gap-2 h-11 px-6 rounded-2xl font-bold transition-all active:scale-95">
                        <Plus className="w-5 h-5" /> New List
                    </Button>
                }
            />

            {!loading && lists.length > 0 && (
                <div className="mb-8 flex items-center justify-between">
                    <div className="relative w-full max-w-sm">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Find a list by name..."
                            className="pl-9 h-12 rounded-2xl border-border/50 bg-card shadow-sm font-medium"
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-48 rounded-[2rem] bg-muted/40 animate-pulse border border-border/50" />
                    ))}
                </div>
            ) : lists.length === 0 ? (
                <EmptyState 
                    title="No lists yet"
                    description="Create lists to segment your contacts and companies for targeted outreach campaigns."
                    icon={ClipboardList}
                    action={
                        <Button 
                            onClick={() => {
                                setFormData({ name: '', description: '', visibility: 'PRIVATE' });
                                setIsCreateModalOpen(true);
                            }} 
                            variant="outline" 
                            className="mt-2 border-border text-primary hover:bg-primary/5 rounded-xl font-bold"
                        >
                            <Plus className="w-4 h-4 mr-2" /> Create First List
                        </Button>
                    }
                />
            ) : filteredLists.length === 0 ? (
                <div className="py-20 text-center">
                    <p className="text-muted-foreground font-medium">No lists match your search.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredLists.map(list => (
                        <Card 
                            key={list._id} 
                            className="group hover:border-primary/50 cursor-pointer transition-all duration-300 shadow-sm hover:shadow-xl bg-card rounded-[2rem] border-border overflow-hidden transform hover:-translate-y-1 flex flex-col h-full"
                            onClick={() => navigate(`/crm/lists/${list._id}`)}
                        >
                            <CardHeader className="flex flex-row items-center justify-between p-6 pb-0">
                                <div className="p-3 rounded-2xl bg-primary/10 text-primary border border-primary/20 shadow-sm whitespace-nowrap">
                                    <ClipboardList className="w-6 h-6" />
                                </div>
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    <StatusBadge 
                                        variant={list.visibility === 'PRIVATE' ? 'default' : 'primary'}
                                        icon={list.visibility === 'PRIVATE' ? Lock : Globe}
                                    >
                                        {list.visibility}
                                    </StatusBadge>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg hover:bg-muted text-muted-foreground">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40 rounded-xl shadow-xl">
                                            <DropdownMenuItem onClick={(e) => openEditModal(e, list)} className="font-medium cursor-pointer py-2.5">
                                                <Edit2 className="w-4 h-4 mr-2 text-muted-foreground" /> Rename
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleDeleteList(list)} className="font-medium cursor-pointer text-destructive focus:text-destructive py-2.5">
                                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </CardHeader>
                            <CardContent className="p-6 flex-1 flex flex-col">
                                <CardTitle className="text-xl font-black tracking-tight group-hover:text-primary transition-colors truncate mb-2">
                                    {list.name}
                                </CardTitle>
                                {list.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                                        {list.description}
                                    </p>
                                )}
                                
                                <div className="mt-auto">
                                    <div className="flex items-center gap-2 mb-6 opacity-60">
                                        <Clock className="w-3.5 h-3.5" />
                                        <span className="text-[10px] font-black uppercase tracking-widest">
                                            Added {formatDistanceToNow(new Date(list.createdAt))} ago
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                                        <div className="flex items-center gap-3">
                                            <div className="flex -space-x-3">
                                                {/* Represent members visually */}
                                                {[...Array(Math.min(3, list.prospectCount || 0))].map((_, i) => (
                                                    <div key={i} className="w-8 h-8 rounded-full bg-primary/10 border-2 border-card flex items-center justify-center text-[10px] font-black shadow-sm text-primary">
                                                        <Users className="w-3 h-3" />
                                                    </div>
                                                ))}
                                                {(list.prospectCount || 0) === 0 && (
                                                    <div className="w-8 h-8 rounded-full bg-muted border-2 border-card flex items-center justify-center shadow-sm">
                                                        <Users className="w-3 h-3 text-muted-foreground opacity-50" />
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-sm font-black tracking-tight text-foreground">
                                                {list.prospectCount || 0} <span className="text-muted-foreground font-bold ml-0.5 opacity-60">Leads</span>
                                            </span>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground group-hover:bg-primary group-hover:text-white transition-all">
                                            <ChevronRight className="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Sheet open={isCreateModalOpen || isEditModalOpen} onOpenChange={(open) => {
                if (!open) {
                    setIsCreateModalOpen(false);
                    setIsEditModalOpen(false);
                    setEditingList(null);
                }
            }}>
                <SheetContent side="right" className="sm:max-w-md rounded-[2.5rem] p-0 overflow-hidden border-border/50 shadow-2xl">
                    <div className="p-8">
                        <SheetHeader className="mb-6 space-y-2">
                            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2">
                                <ClipboardList className="w-6 h-6" />
                            </div>
                            <SheetTitle className="text-2xl font-black">
                                {isEditModalOpen ? 'Rename List' : 'Create New List'}
                            </SheetTitle>
                            <SheetDescription className="text-sm font-medium text-muted-foreground">
                                Lists help you organize contacts for sequences and outreach.
                            </SheetDescription>
                        </SheetHeader>

                        <form onSubmit={isEditModalOpen ? handleEditList : handleCreateList} className="space-y-6">
                            <div className="space-y-2">
                                <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest opacity-80">List Name</label>
                                <Input 
                                    value={formData.name} 
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g. Q3 Tier 1 Prospects"
                                    className="h-12 rounded-xl bg-muted/30 border-2 focus-visible:border-primary font-bold px-4"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest opacity-80">Description <span className="opacity-50 lowercase font-normal">(optional)</span></label>
                                <Input 
                                    value={formData.description} 
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief description of this segment"
                                    className="h-12 rounded-xl bg-muted/30 border-2 focus-visible:border-primary font-medium px-4"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest opacity-80">Visibility</label>
                                <div className="grid grid-cols-2 gap-2 p-1 bg-muted/30 border-2 border-border/50 rounded-2xl">
                                    <button 
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, visibility: 'PRIVATE' }))}
                                        className={`py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${formData.visibility === 'PRIVATE' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                    >
                                        <Lock className="w-3.5 h-3.5" /> Private
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, visibility: 'WORKSPACE_SHARED' }))}
                                        className={`py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 ${formData.visibility === 'WORKSPACE_SHARED' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                    >
                                        <Globe className="w-3.5 h-3.5" /> Shared
                                    </button>
                                </div>
                            </div>
                            <div className="pt-4 flex gap-3">
                                <Button 
                                    type="button" 
                                    variant="ghost" 
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        setIsCreateModalOpen(false);
                                        setIsEditModalOpen(false);
                                    }} 
                                    className="flex-1 h-12 rounded-2xl font-black text-[11px] uppercase tracking-widest"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    type="submit" 
                                    disabled={isSubmitting || !formData.name.trim()}
                                    className="flex-1 h-12 rounded-2xl bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 font-black text-[11px] uppercase tracking-widest border-none"
                                >
                                    {isSubmitting ? 'Saving...' : (isEditModalOpen ? 'Save Changes' : 'Create List')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </SheetContent>
            </Sheet>
        </PageShell>
    );
}
